import { useAppSelector } from "../store/configureStore";
import { useLocation } from "react-router-dom";
import { ORDERSTATUS } from "../models/order";
import { isNarrowEnoughForFullLoad } from "../components/orders/promo/promoPresets";
import useReadOnlyPreset from "./useReadOnlyPreset";
import useHQmanager from "./useHQmanager";

// Bulk actions (row selection + ship / picklists).
// - HQ only needs them on the Approve preset; everywhere else the table stays
//   server-side paginated without bulk.
// - Picker / admin get them on the flow presets (Approve / Manage / Send) and,
//   on the manual Backorder / All path, once the list is narrow enough to be
//   pulled in one full load: a promo code plus a single order type or status
//   (see isNarrowEnoughForFullLoad). Never on the read-only Original preset.
export default function useBulkActionsAvailable(): boolean {
  const { search } = useLocation();
  const preset = new URLSearchParams(search).get("preset");
  const { order_status, order_type, promo_code } = useAppSelector(
    (state) => state.orders.order_params,
  );
  const { user } = useAppSelector((state) => state.account);
  const isHQ = useHQmanager(user);
  const readOnly = useReadOnlyPreset();

  if (isHQ) return preset === "approve";

  const hasStatusFilter =
    typeof order_status === "string" && order_status !== "";
  // Cancelled is terminal — no bulk action applies, so no selection column.
  const isCancelled = order_status === ORDERSTATUS.cancelled;

  // Flow presets keep selection without a promo, once narrowed to their status
  // (approve / set in progress / ship).
  const isFlowPreset =
    preset === "approve" || preset === "handle" || preset === "send";
  if (isFlowPreset) return hasStatusFilter && !isCancelled && !readOnly;

  // Manual path (Backorder / All): selecting to request lists needs a promo
  // code, and the list has to be narrow enough to load in full — otherwise the
  // selection would not survive a page change anyway, since the table stays
  // server-side paginated. See isNarrowEnoughForFullLoad.
  return (
    isNarrowEnoughForFullLoad({ promo_code, order_type, order_status }) &&
    !isCancelled &&
    !readOnly
  );
}
